import React from 'react';
import { useTranslation } from 'react-i18next';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import Link from 'next/link';
import styled from '@emotion/styled';
import { UnstyledButton } from 'vl-common/src/components/UnstyledButton';

const Arrow = () => (
  <div>
    <MdKeyboardArrowLeft size={24} data-testid="page-back-caret" />
  </div>
);

const BackLink = styled(UnstyledButton)`
  display: flex;
  font-weight: 500;
  color: var(--black);
`;

const Heading = styled.div`
  vertical-align: middle;
  line-height: 1;
  font-size: 24px;
  margin-bottom: 12px;
`;

const Subtitle = styled.div`
  font-weight: 500;
  font-size: 12px;
  margin-top: 5px;
`;

const ExtraData = styled.div`
  display: flex;
  padding-left: 20px;
  &:hover {
    cursor: pointer;
  }
`;

const LinkIfAvailable = ({
  link,
  children,
  arrow,
  onClick
}: {
  link?: string;
  children: React.ReactNode;
  arrow?: boolean;
  onClick?: () => void;
}) => {
  const { t } = useTranslation('common');
  if (link) {
    return (
      <Link href={link}>
        <BackLink aria-label={t('Back')} data-testid="page-back-link">
          {arrow && <Arrow />}
          {children}
        </BackLink>
      </Link>
    );
  }

  if (onClick) {
    return (
      <BackLink aria-label={t('Back')} onClick={onClick} data-testid="page-back-link">
        {arrow && <Arrow />}
        {children}
      </BackLink>
    );
  }

  return <div>{children}</div>;
};

type BackButtonProps = {
  url?: string;
  text: string;
  desc?: React.ReactNode;
  extra?: React.ReactNode;
  arrow?: boolean;
  onClick?: () => void;
};

const BackButton = ({ url = undefined, text, desc, extra, arrow = false, onClick = undefined }: BackButtonProps) => (
  <div data-testid="page-title-container">
    <LinkIfAvailable link={url} onClick={onClick} arrow={arrow}>
      <div>
        <Heading data-testid="page-title">{text}</Heading>
        {desc && <Subtitle data-testid="page-subtitle">{desc}</Subtitle>}
      </div>
    </LinkIfAvailable>
    {extra && (
      <ExtraData>
        <Subtitle>{extra}</Subtitle>
      </ExtraData>
    )}
  </div>
);

export default BackButton;
